import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, RotateCw } from 'lucide-react';
import type { StripeEmbeddedCheckoutDto, StripePriceOption } from '@/features/webinar/types';
import { useWebinarT } from '@/hooks/useWebinarT';
import { OFFER_EMBED_IFRAME_ALLOW, OFFER_EMBED_IFRAME_SANDBOX } from '@/lib/offerEmbed';
import { needsStripePriceChoice } from '@/lib/stripePriceOptions';
import {
  StripePriceChoice,
  StripePriceSummary,
} from '@/components/pages/live/attendee/StripePriceChoice';
import {
  STRIPE_CHECKOUT_FRAME_PATH,
  buildStripeCheckoutInit,
  isTrustedStripeCheckoutMessage,
  readStripeCheckoutFrameEvent,
  resolveStripeCheckoutFailure,
  resolveStripeCheckoutOrigin,
} from '@/lib/stripeEmbeddedCheckout';
import { liveService } from '@/services/live.service';
import { cn } from '@/lib/utils';

// Création de la session puis montage du formulaire Stripe : au-delà, on propose de réessayer.
const CHECKOUT_MOUNT_TIMEOUT_MS = 20000;

type CheckoutStatus = 'loading' | 'ready' | 'complete' | 'unavailable' | 'error';

export interface StripeCheckoutAccess {
  webinarId: number;
  authorization: string;
}

interface StripeCheckoutFrameProps {
  offerId: number;
  // Absent hors live (Evergreen) ou sans jeton spectateur : le paiement n'est pas proposé.
  access: StripeCheckoutAccess | null;
  priceOptions?: StripePriceOption[] | null;
  isLightTheme?: boolean;
  buttonColor?: string | null;
  buttonTextColor?: string | null;
}

export default function StripeCheckoutFrame({
  offerId,
  access,
  priceOptions,
  isLightTheme = false,
  buttonColor,
  buttonTextColor,
}: StripeCheckoutFrameProps) {
  const [attempt, setAttempt] = useState(0);
  const options = priceOptions ?? [];
  const needsChoice = needsStripePriceChoice(options);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [confirmedPriceId, setConfirmedPriceId] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  // Sans origine dédiée configurée, on ne propose pas le paiement plutôt que de le servir depuis
  // l'origine du live.
  const checkoutOrigin = resolveStripeCheckoutOrigin(
    import.meta.env.VITE_STRIPE_CHECKOUT_ORIGIN,
    window.location,
  );
  if (!access || !checkoutOrigin) return <CheckoutMessage status="unavailable" />;

  // Une formule retirée par la régie pendant le live renvoie au choix, sur la première.
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.priceId === selectedPriceId),
  );
  const confirmedIndex = options.findIndex((option) => option.priceId === confirmedPriceId);
  if (needsChoice && confirmedIndex < 0) {
    return (
      <StripePriceChoice
        offerId={offerId}
        options={options}
        selectedPriceId={options[selectedIndex].priceId}
        onSelect={setSelectedPriceId}
        onContinue={() => setConfirmedPriceId(options[selectedIndex].priceId)}
        buttonColor={buttonColor}
        buttonTextColor={buttonTextColor}
      />
    );
  }

  const chosenIndex = needsChoice ? confirmedIndex : 0;
  const chosen = options[chosenIndex];
  return (
    <>
      {chosen && !paid && (needsChoice || chosen.recurringInterval) && (
        <StripePriceSummary
          option={chosen}
          index={chosenIndex}
          isLightTheme={isLightTheme}
          onChange={needsChoice ? () => setConfirmedPriceId(null) : undefined}
        />
      )}
      <StripeCheckoutAttempt
        key={`${confirmedPriceId ?? ''}:${attempt}`}
        offerId={offerId}
        priceId={needsChoice ? (confirmedPriceId ?? null) : null}
        access={access}
        checkoutOrigin={checkoutOrigin}
        onRetry={() => setAttempt((previous) => previous + 1)}
        onPaid={setPaid}
      />
    </>
  );
}

function StripeCheckoutAttempt({
  offerId,
  priceId,
  access,
  checkoutOrigin,
  onRetry,
  onPaid,
}: {
  offerId: number;
  // null : le back prend la seule formule de l'offre.
  priceId: string | null;
  access: StripeCheckoutAccess;
  checkoutOrigin: string;
  onRetry: () => void;
  onPaid?: (paid: boolean) => void;
}) {
  const { t } = useWebinarT('webinars');
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [status, setStatus] = useState<CheckoutStatus>('loading');
  const [session, setSession] = useState<StripeEmbeddedCheckoutDto | null>(null);
  const [frameReady, setFrameReady] = useState(false);

  // Le back réutilise la session déjà créée pour ce spectateur et cette formule : un second appel
  // est sans effet, un changement de formule en recrée une.
  useEffect(() => {
    const controller = new AbortController();
    liveService
      .createStripeCheckoutSession(
        access.webinarId,
        offerId,
        access.authorization,
        priceId,
        controller.signal,
      )
      .then(setSession)
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setStatus(resolveStripeCheckoutFailure(error) === 'unavailable' ? 'unavailable' : 'error');
      });
    return () => controller.abort();
  }, [access.webinarId, access.authorization, offerId, priceId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedStripeCheckoutMessage(event, frameRef.current?.contentWindow, checkoutOrigin)) {
        return;
      }
      const frameEvent = readStripeCheckoutFrameEvent(event.data);
      if (frameEvent === 'ready') setFrameReady(true);
      if (frameEvent === 'mounted') setStatus('ready');
      if (frameEvent === 'complete') {
        setStatus('complete');
        onPaid?.(true);
      }
      if (frameEvent === 'error') setStatus('error');
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkoutOrigin, onPaid]);

  useEffect(() => {
    if (!session || !frameReady) return;
    frameRef.current?.contentWindow?.postMessage(buildStripeCheckoutInit(session), checkoutOrigin);
  }, [session, frameReady, checkoutOrigin]);

  useEffect(() => {
    if (status !== 'loading') return;
    const timer = window.setTimeout(() => setStatus('error'), CHECKOUT_MOUNT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  return (
    <div className="relative flex-1 min-h-0 overflow-hidden rounded-2xl bg-white">
      {status !== 'unavailable' && status !== 'complete' && (
        <iframe
          ref={frameRef}
          src={`${checkoutOrigin}${STRIPE_CHECKOUT_FRAME_PATH}`}
          title={t('live.offerEmbed.paymentTitle')}
          sandbox={OFFER_EMBED_IFRAME_SANDBOX}
          allow={OFFER_EMBED_IFRAME_ALLOW}
          referrerPolicy="no-referrer"
          className={cn('absolute inset-0 size-full border-0', status !== 'ready' && 'invisible')}
        />
      )}
      {status !== 'ready' && <CheckoutMessage status={status} onRetry={onRetry} />}
    </div>
  );
}

function CheckoutMessage({
  status,
  onRetry,
}: {
  status: Exclude<CheckoutStatus, 'ready'>;
  onRetry?: () => void;
}) {
  const { t } = useWebinarT('webinars');
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white p-6 text-center text-sm text-[#1f1f1f]">
      {status === 'loading' && (
        <Loader2 className="size-6 animate-spin" aria-label={t('live.offerEmbed.loading')} />
      )}
      {status === 'complete' && (
        <>
          <CheckCircle2 className="size-8 text-success" aria-hidden />
          <p>{t('live.offerEmbed.paymentComplete')}</p>
        </>
      )}
      {status === 'unavailable' && <p>{t('live.offerEmbed.paymentUnavailable')}</p>}
      {status === 'error' && (
        <>
          <p>{t('live.offerEmbed.paymentError')}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="flex items-center gap-1 rounded-full border border-[#1f1f1f]/20 px-3 py-1 text-xs font-medium cursor-pointer hover:bg-black/5"
            >
              <RotateCw className="size-3" aria-hidden />
              {t('live.offerEmbed.retry')}
            </button>
          )}
        </>
      )}
    </div>
  );
}
